/**
 * PR-V0b — adapter from the framework-agnostic `createStageCameraApi`
 * surface to the richer `Scene3DStageProps.StageCameraApi` contract.
 *
 * # Why two surfaces
 *
 * `cameraApi.ts` is intentionally minimal: the entire ZOOM math fits
 * in 311 lines of pure functions. App.tsx, on the other hand, calls
 * a richer surface inherited from the legacy SVG stage:
 *
 *   - `zoomWindow()` — returns `boolean`; starts an interactive
 *     two-corner pick (the user clicks corner 1 and corner 2 in the
 *     viewport, the adapter calls `cameraApi.zoomWindow(c1, c2)`).
 *   - `zoomWindowExact(p1, p2, opts)` — direct call (batch / scripts),
 *     taking DOCUMENT (AutoCAD / WCS) corners; see the frame note below.
 *   - `zoomObject(id?, opts)` — interactive entity pick when `id` is
 *     `undefined`, direct fit when supplied; returns the discriminated
 *     `ZoomObjectResult` ("fitted" / "no-effect" / "pick-started" /
 *     "blocked", #293-4) so the command line can surface each case.
 *   - `zoomPrevious(opts)` — returns `boolean` (`false` on empty
 *     history).
 *   - `cancelZoomPicking()` / `isPickingZoomWindow()` /
 *     `isPickingZoomObject()` / `hasPreviousView()` — picking
 *     state machine inspectors.
 *
 * The adapter owns the picking state machine; the actual fit math
 * stays in `cameraApi.ts` so the unit tests there cover every
 * geometric edge case without spinning up R3F.
 *
 * # Coordinate frame — this adapter is the document→offset-world seam
 *
 * `cameraApi.ts` is pure math over the RENDERER frame: `cameraStateRef` holds
 * the live ortho centre, which is OFFSET-world (every geometry adapter
 * subtracts the locked `coordinateOrigin` for Float32 precision, and
 * `CameraApiBinder` mirrors that same ortho back into the ref). So every
 * ABSOLUTE point handed to the inner API must already be offset-world.
 *
 * The two ZOOM Window entries reach it from opposite frames:
 *
 *   - the INTERACTIVE pick (`zoomWindow()` → `onPickCorner1/2`) receives its
 *     corners from `stageInteractions.clientToWorld`, which unprojects through
 *     that same live ortho — already OFFSET-world. It is passed straight
 *     through; translating it here would double-apply the origin.
 *   - `zoomWindowExact(p1, p2)` is the command-line / script / agent entry
 *     (`commandDispatch/viewCommands.ts` `ZW x1,y1 x2,y2`). Those literals are
 *     what the user reads off the drawing — DOCUMENT (AutoCAD) coordinates —
 *     so they are translated with `applyOffset` here, at the one seam where
 *     they cross into the renderer frame.
 *
 * MEASURED before this translation existed: on a drawing with origin
 * (1e6, 1e6), `ZW 78271,23844 520022,253060` parked the camera centre at
 * (1299147, 1138452) instead of (299147, 138452) — off by exactly the origin.
 *
 * `pan(dx, dy)` needs no such treatment: a DISPLACEMENT is invariant under a
 * pure translation of the frame. `zoomExtents` / `zoomAll` / `zoomObject` take
 * no points at all — their bounds come from the spatial index, which is itself
 * offset-world (`state/spatialBuilder.ts buildSpatialRecordsForSpaces`).
 */

import { applyOffset } from "./coordinateOrigin";
import type {
  StageCameraApi as InnerCameraApi,
  CameraStateSnapshot,
} from "./cameraApi";
import type {
  StageCameraApi as PropsCameraApi,
  ZoomObjectResult,
  ZoomOptions,
} from "./Scene3DStageProps";

/** Picking state owned by the adapter. */
export interface ZoomPickingState {
  mode: "zoom-window" | "zoom-object";
  firstCorner?: { x: number; y: number };
  /** Called on Esc / explicit cancel. */
  onCancel: () => void;
  /** First-corner pick (zoom-window mode). */
  onPickCorner1?: (world: { x: number; y: number }) => void;
  /** Second-corner pick (zoom-window mode). */
  onPickCorner2?: (world: { x: number; y: number }) => void;
  /** Entity pick (zoom-object mode). */
  onPickEntity?: (entityId: string) => void;
}

export interface CameraApiAdapterPrompts {
  /** Shown when zoomWindow() awaits the first corner pick. */
  readonly zoomWindowFirstCorner: string;
  /** Shown when zoomWindow() awaits the opposite corner pick. */
  readonly zoomWindowSecondCorner: string;
  /** Shown when zoomObject() awaits an entity pick on canvas. */
  readonly zoomObjectSelectObjects: string;
}

export interface CameraApiAdapterDeps {
  /** Inner pure-math API. */
  readonly inner: InnerCameraApi;
  /** Currently-selected entity ids (for `zoomObject()` no-arg form). */
  readonly getSelectedIds: () => readonly string[];
  /** Opens the prompt strip in the command line. */
  readonly setZoomPrompt: (prompt: string | null) => void;
  /** Mutable picking-state ref shared with `installStageInteractions`. */
  readonly pickingRef: { current: ZoomPickingState | null };
  /**
   * Push-history hook so PREVIOUS works after every mutation. When called with
   * no argument it pushes the LIVE camera (zoom ops snapshot the pre-zoom view
   * synchronously, before they commit). cmd-pan P2-3 — `pan()` passes an EXPLICIT
   * pre-pan base (the in-flight tween target, or the live camera) so two rapid
   * scripted pans push two DISTINCT snapshots instead of both reading the stale
   * live camera (which the 300ms tween only advances on later frames).
   */
  readonly snapshotForHistory: (state?: CameraStateSnapshot) => void;
  /**
   * cmd-pan P2-3 — the END-STATE target of an in-flight camera tween, or `null`
   * when none is pending. `pan()` reads this to snapshot the correct pre-pan
   * BASE for THIS pan (where this pan STARTS from = the pending tween target if a
   * prior pan/zoom is still tweening, else the live camera). Without it, a second
   * scripted pan fired before the first tween commits would snapshot the stale
   * live camera — identical to the first pan's snapshot — and ZOOM Previous would
   * jump straight to the original view instead of stepping back through each pan.
   * Optional; absent in non-tweened harnesses (then `pan()` snapshots the live
   * camera via the inner API, the immediate-write path).
   */
  readonly getPendingTweenTarget?: () => CameraStateSnapshot | null;
  /**
   * cmd-pan r5 P2-3 — clear any IN-FLIGHT camera tween so a realtime LMB-pan drag
   * that starts mid-tween OWNS the camera (else `CameraApiBinder` keeps applying
   * the stale tween target over the drag → snap-back / wrong ZP snapshot). The
   * stage backs this with `pendingTweenRef.current = null`. Optional; absent in
   * non-tweened harnesses (no pending tween to cancel there → safe no-op).
   */
  readonly clearPendingTween?: () => void;
  /** i18n-translated prompt strings. Caller owns translation. */
  readonly prompts: CameraApiAdapterPrompts;
  /**
   * CC-1c (#284-6) — fired when an INTERACTIVE zoom pick COMPLETES and actually
   * moves the camera (two-corner ZOOM Window second-corner pick, ZOOM Object
   * entity pick). The command-line `ZW`/`ZO` entry only STARTS the pick and
   * cannot know the view changed (the fit happens later, here), so the view-op
   * telemetry must be emitted on this completion callback — not at command
   * dispatch — to record exactly one zoom view-op per completed interactive
   * zoom. Gated on a real camera delta so a picked entity with no resolvable
   * bounds (no view change) records nothing. Owner (App.tsx) wires it to
   * `recordViewOp`; optional so non-telemetry call sites stay unaffected.
   */
  readonly onInteractiveZoomComplete?: (mode: "window" | "object") => void;
}

/**
 * Build the rich `Scene3DStageProps.StageCameraApi` from the inner
 * pure-math API + a small picking state machine.
 */
export function createCameraApiAdapter(
  deps: CameraApiAdapterDeps,
): PropsCameraApi {
  const {
    inner,
    getSelectedIds,
    setZoomPrompt,
    pickingRef,
    snapshotForHistory,
    getPendingTweenTarget,
    clearPendingTween: clearPendingTweenDep,
    prompts,
    onInteractiveZoomComplete,
  } = deps;

  function clearPicking(): void {
    pickingRef.current = null;
    setZoomPrompt(null);
  }

  /**
   * True when an inner fit actually changes the view. The single source of the
   * "did the view change?" delta check used by every caller (direct ZOOM
   * Object, ZOOM Previous, and the interactive-pick completions) so the
   * telemetry / return-value gating never drifts between sites.
   *
   * #293 (FU-TELE, ex-#284-6) — `target` is the snapshot RETURNED by the inner
   * zoom op (the state it wrote synchronously OR scheduled on the tween), and
   * `null` when the op was a no-op. It must NEVER be re-read via
   * `inner.getCamera()` after the call: in the real stage every fit goes
   * through `setCamera(..., { animate: true })`, which `mountStageCameraApi`
   * implements by only scheduling `pendingTweenRef` — `cameraStateRef` (and
   * thus `getCamera()`) is mutated on LATER animation frames, so a synchronous
   * before/after diff of the live ref always read "unchanged" and suppressed
   * the interactive ZOOM Window/Object view-op telemetry (and falsely failed
   * ZOOM Previous / direct ZOOM Object) for every tweened zoom. Comparing
   * `before` against the committed target is correct on both paths, and keeps
   * the emit point consistent with ZE/ZA/ZW-exact, which record their view-op
   * at dispatch while their camera motion is equally tween-deferred (emitting
   * from tween completion instead would lose the event whenever a tween is
   * superseded mid-flight and would skew interactive zooms ~300ms late).
   */
  function fitChangedCamera(
    before: { centerX: number; centerY: number; zoom: number },
    target: { centerX: number; centerY: number; zoom: number } | null,
  ): boolean {
    return (
      target !== null &&
      (before.centerX !== target.centerX ||
        before.centerY !== target.centerY ||
        before.zoom !== target.zoom)
    );
  }

  function isPickingZoomWindow(): boolean {
    return pickingRef.current?.mode === "zoom-window";
  }

  function isPickingZoomObject(): boolean {
    return pickingRef.current?.mode === "zoom-object";
  }

  function cancelZoomPicking(): void {
    if (pickingRef.current) {
      pickingRef.current.onCancel();
      clearPicking();
    }
  }

  // cmd-pan r5 P2-3 — cancel any in-flight camera tween so a realtime LMB-pan
  // drag started mid-tween owns the camera. Delegates to the stage-supplied
  // `clearPendingTween` (nulls `pendingTweenRef`); a no-op in harnesses that
  // schedule no tween (none pending → nothing to cancel).
  function clearPendingTween(): void {
    clearPendingTweenDep?.();
  }

  function zoomExtents(_opts?: ZoomOptions): void {
    snapshotForHistory();
    inner.zoomExtents();
  }

  function zoomAll(_opts?: ZoomOptions): void {
    snapshotForHistory();
    inner.zoomAll();
  }

  /**
   * ZOOM Window with explicit corners in DOCUMENT (AutoCAD / WCS) coordinates
   * — the `ZW x1,y1 x2,y2` command-line / script form. Translated into the
   * renderer's offset-world frame here, the one seam these coordinates cross;
   * the interactive pick path deliberately does NOT go through this function
   * (its corners are already offset-world). See the frame note in the module
   * header.
   */
  function zoomWindowExact(
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    _opts?: ZoomOptions,
  ): void {
    snapshotForHistory();
    const c1 = applyOffset({ x: p1.x, y: p1.y, z: 0 });
    const c2 = applyOffset({ x: p2.x, y: p2.y, z: 0 });
    inner.zoomWindow({ x: c1.x, y: c1.y }, { x: c2.x, y: c2.y });
  }

  function zoomWindow(): boolean {
    if (pickingRef.current) {
      // Already picking — refuse to start a second pass; caller will
      // surface the "blocked" feed line.
      return false;
    }
    setZoomPrompt(prompts.zoomWindowFirstCorner);
    pickingRef.current = {
      mode: "zoom-window",
      onCancel: clearPicking,
      onPickCorner1: (world) => {
        const cur = pickingRef.current;
        if (!cur || cur.mode !== "zoom-window") return;
        cur.firstCorner = world;
        setZoomPrompt(prompts.zoomWindowSecondCorner);
      },
      onPickCorner2: (world) => {
        const cur = pickingRef.current;
        if (!cur || cur.mode !== "zoom-window" || !cur.firstCorner) return;
        snapshotForHistory();
        const before = inner.getCamera();
        const target = inner.zoomWindow(cur.firstCorner, world);
        clearPicking();
        // #284-6 — the interactive window pick completed and moved the view;
        // emit the view-op here (the command-line ZW only started the pick).
        // #293 — gate on the RETURNED fit target, not a re-read of getCamera():
        // the tweened stage defers the camera write to later frames.
        if (fitChangedCamera(before, target)) {
          onInteractiveZoomComplete?.("window");
        }
      },
    };
    return true;
  }

  function zoomObject(
    entityId?: string,
    _opts?: ZoomOptions,
  ): ZoomObjectResult {
    if (entityId !== undefined) {
      // Direct call from script / cmd-line. Judge "did the view change" by
      // the RETURNED fit target (#293): missing bounds → null → "no-effect"
      // so the caller can surface "not found"; under the tweened stage the
      // live camera ref is still pre-zoom here, so it must not be diffed.
      const before = inner.getCamera();
      snapshotForHistory();
      const target = inner.zoomObject([entityId]);
      return fitChangedCamera(before, target) ? "fitted" : "no-effect";
    }
    // No id arg. Already picking (e.g. an interactive ZOOM Window pick is in
    // progress): this call does NOT fit, snapshot history, or start an object
    // pick — it is a blocked no-op (#284-4). Report it as the DISTINCT
    // "blocked" arm (#293-4): the caller's old boolean could not tell
    // "blocked" from "pick started" and told the user to select objects even
    // though clicks still belonged to the previous pick. FU2 (#293 follow-up):
    // this guard must run BEFORE the selection branch — an active pick owns
    // the pointer (AutoCAD parity), so blocked wins over a live selection;
    // previously ZO with a selection still snapshotted + fitted mid-pick.
    if (pickingRef.current) return "blocked";
    // Try selection first, fall back to interactive pick.
    const sel = getSelectedIds();
    if (sel.length > 0) {
      // #293 — same honest gate as the direct-id form: a selection whose ids
      // resolve to no bounds is a no-op fit and must NOT report "fitted"
      // (the old unconditional `true` logged a false zoom view-op for it).
      const before = inner.getCamera();
      snapshotForHistory();
      const target = inner.zoomObject([...sel]);
      return fitChangedCamera(before, target) ? "fitted" : "no-effect";
    }
    // Begin interactive pick.
    setZoomPrompt(prompts.zoomObjectSelectObjects);
    pickingRef.current = {
      mode: "zoom-object",
      onCancel: clearPicking,
      onPickEntity: (id) => {
        snapshotForHistory();
        const before = inner.getCamera();
        const target = inner.zoomObject([id]);
        clearPicking();
        // #284-6 — the interactive object pick completed; emit the view-op
        // only if the picked entity resolved to bounds (the view changes).
        // #293 — gate on the returned target (tween-deferred camera write).
        if (fitChangedCamera(before, target)) {
          onInteractiveZoomComplete?.("object");
        }
      },
    };
    return "pick-started";
  }

  function pan(
    dx: number,
    dy: number,
    opts?: ZoomOptions,
  ): boolean {
    // PAN pushes the pre-pan view onto the ZOOM-Previous ring (AutoCAD reverses
    // a PAN with ZOOM Previous), then drives the world-space displacement.
    // #293 — judge "did the view change?" by the snapshot the inner op
    // RETURNED (the committed target / null on zero displacement), never a
    // re-read of getCamera(): the tweened stage defers the live camera write to
    // later frames, so a synchronous before/after diff always reads "unchanged"
    // and would suppress the honest `pan` view-op (the #269 lesson, #R4).
    //
    // cmd-pan P2-3 — capture the pre-pan BASE for THIS pan FIRST (the state this
    // pan starts FROM = the in-flight tween's end-state if a prior pan/zoom is
    // still tweening, else the live camera), run the inner op, and push THAT
    // captured base onto the ZOOM-Previous ring ONLY when the pan actually moved
    // (non-null target). Two rapid scripted `-PAN`s thus push two DISTINCT
    // snapshots (the original view, then the first pan's target) instead of both
    // re-reading the STALE live camera (which the 300ms tween only advances on
    // later frames) — so ZOOM Previous steps back through each pan, not straight
    // to the original. A `-PAN p,p` / `pan(0,0)` returns null and pushes nothing,
    // so it does not pollute the ring with a duplicate that the next ZOOM
    // Previous would pop + report as "no restore" (consuming a real entry).
    // cmd-pan P2-2 — judge "did the view change?" against the SAME `base` (the
    // effective current view = pending tween target if a prior pan/zoom is still
    // in flight, else the live camera), NOT a fresh `inner.getCamera()`. A
    // back-to-back `-PAN` that cancels an in-flight pan lands on a target that
    // EQUALS the stale live camera but DIFFERS from the pending base: `inner.pan`
    // schedules a real non-zero displacement + pushes history, so the method MUST
    // report the move. Diffing against the live camera here would read "equal" →
    // App omits the pan audit + shows the spurious "no move" message.
    const base = getPendingTweenTarget?.() ?? inner.getCamera();
    const target = inner.pan(dx, dy, opts);
    if (target === null) {
      return false;
    }
    snapshotForHistory(base);
    return fitChangedCamera(base, target);
  }

  function snapshotView(state?: CameraStateSnapshot): void {
    // Push a view onto the ZOOM-Previous ring without moving the camera
    // (interactive realtime-pan: the LMB drag pans via panByPixels directly,
    // outside this adapter, so the pre-pan view is captured here so ZOOM
    // Previous can restore it, AutoCAD parity). cmd-pan P2-3 — the stage passes
    // an EXPLICIT pre-drag snapshot captured at drag START and pushes it ONLY on
    // a CONFIRMED non-zero completion; pushing the LIVE camera here instead would
    // (a) at drag-end read the POST-drag view and (b) at first-move pollute the
    // ring even for a zero-net drag. Falls back to the live camera when no state
    // is supplied (non-stage callers / legacy harnesses).
    snapshotForHistory(state);
  }

  function zoomPrevious(_opts?: ZoomOptions): boolean {
    // Judge the restore by the snapshot the inner op POPPED + committed
    // (null = empty history ring). #293 — under the tweened stage the live
    // camera ref is unchanged until later frames, so the old before/after
    // getCamera() probe always returned false and ZP reported "no previous
    // view" even while the camera was visibly tweening back.
    //
    // cmd-pan P2-2 — compare against the EFFECTIVE current view, not the stale
    // live camera. After a PAN (or a ZOOM fit) the inner op only SCHEDULES the
    // 300ms tween, so `inner.getCamera()` still reports the PRE-op view for the
    // whole tween. If `Z P` runs before that tween settles, diffing the popped
    // snapshot against the stale live camera reads "equal" → ZP wrongly reports
    // "no previous view" AND consumes the entry instead of reversing the op. The
    // pending tween target is the real current view while a tween is in flight
    // (the same "effective current view" the pan snapshot base uses).
    const before = getPendingTweenTarget?.() ?? inner.getCamera();
    const target = inner.zoomPrevious();
    return fitChangedCamera(before, target);
  }

  function hasPreviousView(): boolean {
    // The inner API has no `length` getter; fall back to a heuristic
    // — we let the caller see the result of `zoomPrevious()` and
    // optimistically report `true`. App.tsx already surfaces the
    // "no previous view" feed line based on the bool returned by
    // `zoomPrevious`, so a permissive `hasPreviousView` is safe.
    return true;
  }

  return {
    zoomExtents,
    zoomAll,
    zoomWindow,
    zoomWindowExact,
    zoomPrevious,
    zoomObject,
    pan,
    snapshotView,
    cancelZoomPicking,
    clearPendingTween,
    hasPreviousView,
    isPickingZoomWindow,
    isPickingZoomObject,
  };
}
